<template>
  <md-card class="mt-30" :class="analysisScoreClass">
    <div class="analysis-header-row" :class="{ 'no-eval-padding': !hasEvaluation }" @click="toggle">
      <div class="analysis-header-left">
        <strong class="upper-word">Request Evaluation</strong>
        <span v-if="recommendedScore" class="approval-verdict-badge" :class="{
          'verdict-approve': recommendedScore === 'Approve',
          'verdict-caution': recommendedScore === 'Approve with Caution',
          'verdict-reject': recommendedScore === 'Do Not Approve'
        }">
          <i :class="{
            'fa fa-check-circle': recommendedScore === 'Approve',
            'fa fa-exclamation-circle': recommendedScore === 'Approve with Caution',
            'fa fa-times-circle': recommendedScore === 'Do Not Approve'
          }"></i>
          {{ verdictLabel }}
        </span>
        <span v-else-if="hasEvaluation" class="approval-verdict-badge verdict-caution">
          <i class="fa fa-exclamation-circle"></i>
          Incomplete
        </span>
        <span v-else class="approval-verdict-badge verdict-none">
          No Evaluation Yet
        </span>
      </div>
      <i class="fa fa-chevron-down analysis-chevron" :class="{ 'analysis-chevron--open': isOpen }"></i>
   </div>
    <p v-if="summaryText && !isOpen" class="analysis-summary-preview">{{ summaryText }}</p>
    <transition name="slide">
      <div v-show="isOpen" class="analysis-body">

        <p v-if="!isEvaluating && !errorMessage && evaluatedAtLabel" class="analysis-timestamp">
          <i class="far fa-clock" style="margin-right: 4px;"></i>Last evaluated {{ evaluatedAtLabel }}
        </p>

        <!-- LOADING -->
        <div v-if="isEvaluating" class="analysis-failed-state"
          style="background: rgba(99,102,241,0.06); border-color: rgba(99,102,241,0.2);">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="20" :md-stroke="2" />
          <p class="analysis-explanation" style="margin-top: 10px; margin-bottom: 0;">Analyzing request...</p>
        </div>

        <!-- ERROR -->
        <div v-else-if="errorMessage" class="analysis-failed-state">
          <p class="analysis-explanation" style="margin-bottom: 12px;">
            <i class="fa fa-exclamation-triangle" style="color: #e65100; margin-right: 6px;"></i>
            {{ errorMessage }}
          </p>
          <button type="button" class="generate-eval-btn" :disabled="isEvaluating" @click.stop="generate">
            <i class="fa fa-rotate-left" style="margin-right: 6px;"></i>
            Try again
          </button>
        </div>

        <!-- NO EVALUATION YET -->
        <div v-else-if="!hasEvaluation" class="analysis-failed-state">
          <p class="analysis-explanation" style="margin-bottom: 12px;">
            No evaluation generated yet for this request.
          </p>
          <button type="button" class="generate-eval-btn" :disabled="isEvaluating" @click.stop="generate">
            <i class="fa fa-magic" style="margin-right: 6px;"></i>
            Generate evaluation
          </button>
        </div>

        <!-- FAILED — has evaluation but no score -->
        <div v-else-if="!recommendedScore" class="analysis-failed-state">
          <p class="analysis-explanation" style="margin-bottom: 12px;">
            <i class="fa fa-exclamation-triangle" style="color: #e65100; margin-right: 6px;"></i>
            The evaluation could not be completed. This may be due to a temporary service error.
          </p>
          <button type="button" class="generate-eval-btn" :disabled="isEvaluating" @click.stop="generate">
            <i class="fa fa-rotate-left" style="margin-right: 6px;"></i>
            {{ isEvaluating ? 'Re-evaluating...' : 'Re-evaluate' }}
          </button>
        </div>

        <!-- CONTENT -->
        <div v-else>
          <p class="analysis-explanation" style="margin-bottom: 12px; color: #444;">{{ summaryText }}</p>
          <hr style="border: none; border-top: 1px solid rgba(0,0,0,0.08); margin: 0 0 14px 0;" />

          <div v-for="(item, index) in activeEvaluation.all_results" :key="index">

            <div v-if="item.sentiment" class="analysis-section">
              <div class="analysis-section-header">
                <i class="fa fa-heart"></i>
                <span>Sentiment</span>
              </div>
              <div class="analysis-section-body">
                <div class="analysis-badges-row"
                  v-if="item.sentiment.sentiment_values && item.sentiment.sentiment_values.length">
                  <span v-for="val in item.sentiment.sentiment_values" :key="val" class="sentiment-chip">{{ val
                  }}</span>
                </div>
                <template v-if="item.sentiment.explanation && item.sentiment.explanation.length">
                  <p v-if="Array.isArray(item.sentiment.explanation)" v-for="(line, idx) in item.sentiment.explanation"
                    :key="idx" class="analysis-explanation">{{ line }}</p>
                  <p v-else class="analysis-explanation">{{ item.sentiment.explanation }}</p>
                </template>
                <p v-else-if="item.sentiment.raw" class="analysis-explanation"
                  v-html="formatSentimentRaw(item.sentiment.raw)"></p>
              </div>
            </div>

            <div v-else-if="item.good_intents" class="analysis-section">
              <div class="analysis-section-header">
                <i class="fa fa-shield-alt"></i>
                <span>Good Intents</span>
              </div>
              <div class="analysis-section-body">
                <div class="analysis-badges-row">
                  <div class="analysis-badge" :class="ratingBadgeClass(item.good_intents.non_marketing_email)">
                    <i :class="ratingIconClass(item.good_intents.non_marketing_email)"></i>
                    Genuine Email
                  </div>
                  <div class="analysis-badge" :class="ratingBadgeClass(item.good_intents.non_phishing_email)">
                    <i :class="ratingIconClass(item.good_intents.non_phishing_email)"></i>
                    Safe Email
                  </div>
                </div>
                <p class="analysis-explanation">{{ item.good_intents.marketing_explanation }}</p>
                <p class="analysis-explanation">{{ item.good_intents.phishing_explanation }}</p>
              </div>
            </div>

          </div>
        </div>

      </div>
    </transition>
  </md-card>
</template>

<script>
export default {
  name: 'ApprovalGuidanceCard',
  props: {
    evaluation: { type: Object, default: null },
    requestText: { type: String, default: '' },
  },
  data() {
    return {
      isOpen: false,
      isEvaluating: false,
      errorMessage: '',
      localEval: null,
    };
  },
  computed: {
    verdictLabel() {
      if (this.recommendedScore === 'Approve') return 'PASS';
      if (this.recommendedScore === 'Approve with Caution') return 'CAUTION';
      if (this.recommendedScore === 'Do Not Approve') return 'FAIL';
      return this.recommendedScore;
    },
    evaluatedAtLabel() {
      const e = this.activeEvaluation;
      const raw = e && e._evaluatedAt;
      if (!raw) return '';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      const diffMs = Date.now() - d.getTime();
      const min = Math.round(diffMs / 60000);
      if (min < 1) return 'just now';
      if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
      const hr = Math.round(min / 60);
      if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
      const days = Math.round(hr / 24);
      if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    activeEvaluation() {
      return this.localEval || this.evaluation;
    },
    hasEvaluation() {
      const e = this.activeEvaluation;
      if (!e || e.error) return false;
      return !!(e.summary || e.all_results || e.recommended);
    },
    recommendedScore() {
      const e = this.activeEvaluation;
      if (!e || !e.recommended) return null;
      return typeof e.recommended.score === 'string' ? e.recommended.score : null;
    },
    analysisScoreClass() {
      if (this.recommendedScore === 'Do Not Approve') return 'analysis-score--rejected';
      if (this.recommendedScore === 'Approve with Caution') return 'analysis-score--caution';
      if (this.recommendedScore === 'Approve') return 'analysis-score--approved';
      return '';
    },
    summaryText() {
      const e = this.activeEvaluation;
      if (!e || !e.summary) return '';
      return e.summary.summary_of_results || '';
    },
  },
  watch: {
    evaluation: {
      immediate: true,
      handler(val) {
        if (val && !val._evaluatedAt) {
          this.localEval = { ...val, _evaluatedAt: new Date().toISOString() };
        }
      }
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    // Good-intent ratings answer "is this NOT marketing/phishing?" — Yes/Likely are safe.
    ratingBadgeClass(value) {
      const v = (value || '').toLowerCase();
      if (v === 'yes' || v === 'likely') return 'badge-green';
      if (v === 'unlikely') return 'badge-yellow';
      if (v === 'no') return 'badge-red';
      return 'badge-default';
    },
    ratingIconClass(value) {
      const v = (value || '').toLowerCase();
      if (v === 'yes' || v === 'likely') return 'fa fa-check';
      if (v === 'no') return 'fa fa-times';
      return 'fa fa-question';
    },
    formatYesNo(value) {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'string') {
        const v = value.toLowerCase();
        if (v === 'yes' || v === 'true') return 'Yes';
        if (v === 'no' || v === 'false') return 'No';
        return value;
      }
      return String(value);
    },
    formatSentimentRaw(rawText) {
      if (!rawText) return '';
      return rawText.replace(/\n/g, '<br>');
    },
    async generate() {
      if (!this.requestText) {
        this.errorMessage = 'No request text available to evaluate.';
        return;
      }
      this.isEvaluating = true;
      this.errorMessage = '';
      this.$emit('generate');
    },
    setEvaluation(data) {
      this.localEval = data;
      this.isEvaluating = false;
      this.errorMessage = '';
    },
    setError(msg) {
      this.isEvaluating = false;
      this.errorMessage = msg || 'Failed to generate evaluation. Please try again.';
    },
    setLoading(val) {
      this.isEvaluating = val;
    },
  },
};
</script>


<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.3s ease, opacity 0.25s ease;
  overflow: hidden;
}
.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-enter-to,
.slide-leave {
  max-height: 1000px;
  opacity: 1;
}

.response-eval-btn {
  background: #4c8cf5;
  margin-bottom: 14px;
  margin-top: 14px;
}

.analysis-timestamp {
  font-size: 12px;
  color: #888;
  margin: 12px 0 12px 0;
}

.analysis-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 0px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.no-eval-padding {
  padding: 18px 12px 12px 12px;
}
.analysis-header-row:hover {
  background: rgba(0,0,0,0.02);
}
.analysis-header-left {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex: 1;
}
.analysis-header-left strong {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #333;
  line-height: 1;
  margin: 0;
  padding: 4px 3px 3px 3px;
}
.analysis-header-row * {
  box-sizing: border-box;
}
.analysis-body {
  padding: 0 16px 16px 16px;
}
.analysis-summary-preview {
  padding: 8px 16px 12px 16px;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #555;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.analysis-chevron {
  color: #888;
  font-size: 14px;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.analysis-chevron--open {
  transform: rotate(180deg);
}
.generate-eval-btn {
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: filter 0.15s ease;
}
.generate-eval-btn:hover { filter: brightness(1.08); }
.generate-eval-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.generate-eval-btn:focus { outline: none; }
.generate-eval-btn--ghost {
  background: transparent;
  color: #6366f1;
  border: 1px solid #6366f1;
  font-size: 12px;
  padding: 5px 14px;
}
.generate-eval-btn--ghost:hover { background: rgba(99,102,241,0.06); filter: none; }
.verdict-none {
  background-color: rgba(0, 0, 0, 0.06);
  color: #888;
}
.analysis-failed-state {
  padding: 16px;
  background: rgba(242, 192, 87, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(242, 192, 87, 0.3);
  text-align: center;
}
.sentiment-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background-color: rgba(0, 0, 0, 0.07);
  color: #444;
  text-transform: capitalize;
}
.badge-yellow { background-color: rgba(242, 192, 87, 0.25); color: #e65100; }
.approval-verdict-badge {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.2px;
  border: 1px solid transparent;
}
.approval-verdict-badge i {
  line-height: 1;
  display: inline-flex;
  align-items: center;
}
.verdict-approve  { background-color: rgba(46, 125, 50, 0.15);  color: #2e7d32; }
.verdict-caution  { background-color: rgba(242, 192, 87, 0.25);  color: #e65100; }
.verdict-reject   { background-color: rgba(198, 40, 40, 0.15);  color: #c62828; }
.analysis-section {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  background: white;
}
.analysis-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f5f5f5;
  font-weight: 600;
  font-size: 14px;
  color: #444;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.analysis-section-header i { color: #666; font-size: 14px; }
.analysis-section-body { padding: 12px 14px; }
.analysis-badges-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.analysis-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.badge-green { background-color: rgba(46, 125, 50, 0.12);  color: #2e7d32; }
.badge-red   { background-color: rgba(198, 40, 40, 0.12);  color: #c62828; }
.analysis-explanation {
  font-size: 13px;
  color: #555;
  line-height: 1.6;
  margin: 4px 0;
}
.analysis-score--rejected {
  border-left: 4px solid #c62828 !important;
  background-color: rgba(198, 40, 40, 0.05) !important;
}
.analysis-score--caution {
  border-left: 4px solid #e65100 !important;
  background-color: rgba(242, 192, 87, 0.07) !important;
}
.analysis-score--approved {
  border-left: 4px solid #2e7d32 !important;
  background-color: rgba(46, 125, 50, 0.05) !important;
}


.badge-default {
  background-color: rgba(0, 0, 0, 0.06);
  color: #666;
}
</style>



// WEBPACK FOOTER //
// src/components/ApprovalGuidanceCard.vue